import {ButtonGroup, Dropdown, Spinner} from 'react-bootstrap'
import {LinkContainer} from 'react-router-bootstrap'
import {Icons} from './Icons.jsx'
import {IconContext} from 'react-icons'
import PropTypes from 'prop-types'
import styled from 'styled-components'

const Wrapper = styled.div`
    .wrapper:hover {
        background-color: #5c636a;
    }
`

const styles = {
    menuIcon: {
        lineHeight: '1',
        paddingTop: '5px',
        paddingBottom: '4px',
        paddingLeft: '4px',
        paddingRight: '4px',
        display: 'inline-block',
    },
    icon: {
        display: 'inline-block',
        marginRight: '6px',
        marginTop: '2px',
        marginBottom: '2px',
        verticalAlign: '2px'
    }
}

export const ActionsDropdown = ({
    show = true,
    actions = [],
    icon,
    title = 'Więcej akcji',
    variant = 'secondary',
    size = 'sm',
    dynamicState,
    onFetch,
    itemId,
    renderDynamicItem,
    dynamicHeader,
    loadingText = 'Pobieranie danych...',
    emptyDynamicText = 'Brak danych',
}) => {
    if (!show) return null

    const visibleActions = actions.filter(a => a.show !== false)
    const hasDynamicContent = !!onFetch
    const isLoading = dynamicState?.loading === itemId
    const dynamicItems = dynamicState?.data?.[itemId] || []

    const handleToggle = (isOpen) => {
        if (isOpen && onFetch && !dynamicState?.data?.[itemId]) {
            onFetch(itemId)
        }
    }

    const renderActionItem = (action, idx) => {
        const content = (
            <>
                {action.icon && (
                    <div style={styles.icon}>
                        <IconContext.Provider value={{size: 18}}>
                            {action.icon}
                        </IconContext.Provider>
                    </div>
                )}
                {action.label}
            </>
        )

        if (action.to) {
            return (
                <LinkContainer key={`action-${idx}`} to={action.to}>
                    <Dropdown.Item
                        disabled={action.disabled}
                        title={action.title || action.label}
                        className={action.variant ? `text-${action.variant}` : undefined}
                    >
                        {content}
                    </Dropdown.Item>
                </LinkContainer>
            )
        }

        return (
            <Dropdown.Item
                key={`action-${idx}`}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.title || action.label}
                className={action.variant ? `text-${action.variant}` : undefined}
            >
                {content}
            </Dropdown.Item>
        )
    }

    const renderDynamicContent = () => {
        if (!hasDynamicContent) return null

        if (isLoading) {
            return (
                <Dropdown.Item disabled>
                    <div className={'d-flex align-items-center gap-2'}>
                        <Spinner animation={'border'} size={'sm'} />
                        {loadingText}
                    </div>
                </Dropdown.Item>
            )
        }

        if (dynamicItems.length === 0) {
            return (
                <Dropdown.Item disabled>
                    {emptyDynamicText}
                </Dropdown.Item>
            )
        }

        return (
            <>
                {dynamicHeader && <Dropdown.Header>{dynamicHeader}</Dropdown.Header>}
                {dynamicItems.map((item, idx) => {
                    if (renderDynamicItem) {
                        return renderDynamicItem(item, idx)
                    }
                    return (
                        <Dropdown.Item key={`dynamic-${idx}`} disabled>
                            {item.label || item.numer || `Element ${idx + 1}`}
                        </Dropdown.Item>
                    )
                })}
            </>
        )
    }

    return (
        <Dropdown as={ButtonGroup} onToggle={handleToggle}>
            <Wrapper className={'d-flex gap-0 mx-auto'} title={title}>
                <Dropdown.Toggle
                    className={'border-0 py-0 wrapper'}
                    size={size}
                    variant={variant}
                >
                    <div style={styles.menuIcon}>
                        <IconContext.Provider value={{size: 22}}>
                            {icon || <Icons.dotsVertical />}
                        </IconContext.Provider>
                    </div>
                </Dropdown.Toggle>
            </Wrapper>
            <Dropdown.Menu
                variant={variant}
                renderOnMount={true}
                popperConfig={{
                    strategy: 'fixed',
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top', 'bottom'],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'viewport',
                            },
                        },
                        {
                            name: 'computeStyles',
                            options: {
                                adaptive: false,
                            },
                        },
                    ],
                }}
            >
                {visibleActions.map(renderActionItem)}
                {visibleActions.length > 0 && hasDynamicContent && dynamicItems.length > 0 && <Dropdown.Divider />}
                {renderDynamicContent()}
            </Dropdown.Menu>
        </Dropdown>
    )
}

ActionsDropdown.propTypes = {
    show: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.shape({
        show: PropTypes.bool,
        to: PropTypes.string,
        onClick: PropTypes.func,
        icon: PropTypes.node,
        label: PropTypes.string.isRequired,
        title: PropTypes.string,
        disabled: PropTypes.bool,
        variant: PropTypes.string,
    })),
    icon: PropTypes.node,
    title: PropTypes.string,
    variant: PropTypes.string,
    size: PropTypes.oneOf(['lg', 'sm']),
    dynamicState: PropTypes.shape({
        loading: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        data: PropTypes.object,
    }),
    onFetch: PropTypes.func,
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    renderDynamicItem: PropTypes.func,
    dynamicHeader: PropTypes.string,
    loadingText: PropTypes.string,
    emptyDynamicText: PropTypes.string,
}